const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const lenBetween = (v = "", min, max) =>
    typeof v === "string" && v.trim().length >= min && v.trim().length <= max;

export const isPositiveNumber = (v) => {
    if (v === "" || v === null || v === undefined) return false;
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
};

const isOptionalArrayOfIds = (arr) => {
    if (isEmpty(arr)) return true;
    if (!Array.isArray(arr)) return false;
    return arr.every((x) => {
        if (x == null) return false;
        const s = String(x);
        return s.length > 0 && !/^\s+$/.test(s);
    });
};

const isValidUrl = (v) => {
    if (isEmpty(v)) return true;
    try {
        new URL(v);
        return true;
    } catch (_) {
        try {
            new URL(`http://${v}`);
            return true;
        } catch {
            return false;
        }
    }
};

const isEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(v || "").trim());

export const isPhoneValid = (phone) => {
    if (isEmpty(phone)) return false;
    const s = String(phone).replace(/[^\d+]/g, "");
    return /^\+?\d{7,15}$/.test(s);
};

export const buildValidation = (t) => ({
    name: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "150"),
        pattern: (v) => lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    phone: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.customers.form.invalid_phone,
        pattern: (v) => isPhoneValid(v),
        error: false,
        skipped: false,
    },
    email: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.customers.form.invalid_email,
        pattern: (v) => lenBetween(v, 6, 254) && isEmail(v),
        error: false,
        skipped: false,
    },
    industry: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        getVal: (cd) => cd.Industry,
        pattern: (v) => !!v,
        error: false,
        skipped: false,
    },
    location: {
        required: false,
        invalidMessage: t.customers.form.invalid_locations,
        pattern: (v) => isOptionalArrayOfIds(v),
        error: false,
        skipped: false,
    },
    address: {
        required: false,
        invalidMessage: t.customers.form.invalid_address,
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 250),
        error: false,
        skipped: false,
    },
    website: {
        required: false,
        invalidMessage: t.customers.form.invalid_website,
        pattern: (v) => isEmpty(v) || (lenBetween(v, 0, 255) && isValidUrl(v)),
        error: false,
        skipped: false,
    },
    serviceType: {
        required: false,
        invalidMessage:
            t.customers.form.invalid_service_type,
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    description: {
        required: false,
        invalidMessage: t.customers.form.invalid_description,
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 1000),
        error: false,
        skipped: false,
    },

    contact_name: {
        required: false,
        invalidMessage: t.customers.form.invalid_contact_name || "Invalid name",
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    contact_phone: {
        required: false,
        invalidMessage: t.customers.form.invalid_phone,
        pattern: (v) => isEmpty(v) || isPhoneValid(v),
        error: false,
        skipped: false,
    },
    contact_email: {
        required: false,
        invalidMessage: t.customers.form.invalid_email,
        pattern: (v) => isEmpty(v) || (lenBetween(v, 6, 254) && isEmail(v)),
        error: false,
        skipped: false,
    },

    billing_name: {
        required: false,
        invalidMessage: t.customers.form.invalid_billing_name ,
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    billing_address: {
        required: false,
        invalidMessage: t.customers.form.invalid_address,
        pattern: (v) => isEmpty(v) || lenBetween(v, 3, 250),
        error: false,
        skipped: false,
    },
    billing_addressLine2: {
        required: false,
        invalidMessage: t.customers.form.invalid_addressLine,
        pattern: (v) => isEmpty(v) || lenBetween(v, 0, 250),
        error: false,
        skipped: false,
    },
    billing_addressLine3: {
        required: false,
        invalidMessage: t.customers.form.invalid_addressLine,
        pattern: (v) => isEmpty(v) || lenBetween(v, 0, 250),
        error: false,
        skipped: false,
    },
    billing_postal_code: {
        required: false,
        invalidMessage: t.customers.form.invalid_postal_code ,
        pattern: (v) => isEmpty(v) || lenBetween(v, 0, 20),
        error: false,
        skipped: false,
    },
    billing_rate: {
        required: false,
        invalidMessage: t.customers.form.invalid_rate ,
        pattern: (v) => isEmpty(v) || Number(v) === 0 || isPositiveNumber(v),
        error: false,
        skipped: false,
    },
});


const computeValidation = (value, rule, requiredFallback = "Required") => {
    const empty = isEmpty(value);
    const passes = rule.pattern ? rule.pattern(value) : true;

    const error = rule.required ? (empty || (!empty && !passes)) : (!empty && !passes);
    const message =
        empty && rule.required
            ? rule.requiredMessage ?? requiredFallback
            : !passes
                ? rule.invalidMessage ?? rule.message ?? "Invalid value"
                : "";

    return { error, message, skipped: true };
};

export const makeOnBlurFactory = ({ getCustomerData, setValidation /*, t*/ }) => {
    return (key, valueSelector = (cd) => {
        return cd[key];
    }) => () => {
        setValidation((prev) => {
            const rule = prev[key];
            if (!rule) return prev;
            const cd = getCustomerData();
            const selected = rule.getVal ? rule.getVal(cd) : valueSelector(cd);
            return { ...prev, [key]: { ...rule, ...computeValidation(selected, rule) } };
        });
    };
};

export const makeOnValuesChangeFactory = ({
                                              getCustomerData,
                                              setCustomerData,
                                              setValidation,
                                              getOptions,
                                              setOptions,
                                              setInitChange,
                                              vKeyOf = (n) => (n === "Industry" ? "industry" : n),
                                          }) => {
    return (name) => (event, data) => {
        const options = typeof getOptions === "function" ? getOptions() : undefined;
        const customerData = typeof getCustomerData === "function" ? getCustomerData() : {};

        if (typeof setInitChange === "function") {
            setInitChange(true);
        }

        const dropdownFields = [
            "billing_currency",
            "countryCode",
            "country",
            "city",
            "location",
            "Industry",
        ];

        let currentFieldValue = "";

        if (!dropdownFields.includes(name)) {
            currentFieldValue = event?.target?.value ?? "";

            setCustomerData((prev) => ({ ...prev, [name]: currentFieldValue }));

            if (customerData.isSame) {
                if (name === "name") {
                    setCustomerData((prev) => ({
                        ...prev,
                        contact_name: currentFieldValue,
                        name: currentFieldValue,
                    }));
                } else if (name === "email") {
                    setCustomerData((prev) => ({
                        ...prev,
                        contact_email: currentFieldValue,
                        email: currentFieldValue,
                    }));
                } else if (name === "phone") {
                    setCustomerData((prev) => ({
                        ...prev,
                        contact_phone: currentFieldValue,
                        phone: currentFieldValue,
                    }));
                }
            }
        }

        if (dropdownFields.includes(name)) {
            const value = data?.value;
            currentFieldValue = value;

            if (name === "country") {
                setCustomerData((prev) => ({ ...prev, country: value, city: "" }));
            } else if (name === "location") {
                const selectedOptions =
                    options?.locations?.initOptions?.filter((opt) => value?.includes(opt.value)) || [];

                let newSelectedValues = Array.isArray(value) ? [...value] : [];
                let allSelectedValues  = Array.isArray(value) ? [...value] : [];
                let newInitOptions = [...(options?.locations?.initOptions ?? [])];

                selectedOptions.forEach((opt) => {
                    if (!opt.disabled) {
                        const subLocations =
                            options?.locations?.initOptions?.filter((sub) => sub.parentId === opt.key) || [];

                        allSelectedValues = [
                            ...allSelectedValues,
                            ...subLocations.map((s) => s.value),
                        ];

                        if (subLocations.length > 0) {
                            newSelectedValues = newSelectedValues.filter(
                                (v) => !subLocations.some((s) => s.value === v)
                            );
                        }

                        newInitOptions = newInitOptions.filter((initOpt) => initOpt.parentId !== opt.key);
                    }
                });

                setCustomerData((prev) => ({
                    ...prev,
                    [name]: [...new Set(newSelectedValues)],
                    selectedLocation: [...new Set(allSelectedValues)],
                }));

                if (typeof setOptions === "function") {
                    setOptions((prev) => ({
                        ...prev,
                        locations: {
                            ...prev.locations,
                            options: newInitOptions,
                        },
                    }));
                }
            } else {
                setCustomerData((prev) => ({ ...prev, [name]: value }));
            }
        }

        setValidation((prev) => {
            const key = vKeyOf(name);
            const rule = prev[key];
            if (!rule) return prev;

            const next = {
                ...prev,
                [key]: {
                    ...rule,
                    error: !rule.pattern(currentFieldValue),
                },
            };
            return next;
        });
    };
};


export const makeValidateOnSubmitFactory = ({
                                                getCustomerData,
                                                validation,
                                                setValidation,

                                            }) => {
    return () => {
        const cd = getCustomerData();
        const next = { ...validation };
        const keys = Object.keys(next);

        keys.forEach((k) => {
            const rule = next[k];
            if (!rule) return;
            const rawVal = rule.getVal ? rule.getVal(cd) : cd[k];
            const res = computeValidation(rawVal, rule);
            next[k] = { ...rule, ...res };
        });

        setValidation(next);
        return keys.some((k) => next[k]?.error);
    };
};
